import {IdField, MetadataField, ServerTimestampSentinelValue} from '../models/Entity';

/**
 * Predicate function to check if a value should be preserved as-is during deep sanitization.
 * This is used to preserve Firebase-specific types like Date, Timestamp, and FieldValue.
 */
export type IsPreservedType = (value: unknown) => boolean;

/**
 * Factory function to create a server timestamp sentinel value.
 * Returns the appropriate FieldValue.serverTimestamp() for the Firebase SDK being used.
 */
export type CreateServerTimestamp = () => unknown;

/**
 * Recursively sanitizes a value, removing undefined values from objects and arrays.
 * Preserves values that match the isPreservedType predicate (e.g., Date, Timestamp, FieldValue).
 *
 * @param value the value to sanitize.
 * @param isPreservedType predicate to check if a value should be preserved as-is.
 * @returns a sanitized deep copy of the value.
 */
export function deepSanitize(value: unknown, isPreservedType: IsPreservedType): unknown {
  if (value === null || value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => deepSanitize(item, isPreservedType));
  }
  if (isPreservedType(value)) {
    return value;
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return Object.keys(obj).reduce(
      (sanitized, key) => {
        if (obj[key] !== undefined) {
          sanitized[key] = deepSanitize(obj[key], isPreservedType);
        }
        return sanitized;
      },
      {} as Record<string, unknown>,
    );
  }
  return value;
}

/**
 * Makes a deep copy of the given entity, sanitizing fields that are not compatible with Firestore.
 * Removes undefined values and the id field (which is stored in the document path).
 *
 * @param entity the entity to sanitize.
 * @param isPreservedType predicate to check if a value should be preserved as-is.
 * @returns a sanitized deep copy of the given entity.
 */
export function sanitizeForWrite(
  entity: Record<string, unknown>,
  isPreservedType: IsPreservedType,
): Record<string, unknown> {
  return Object.keys(entity).reduce(
    (sanitized, key) => {
      // Remove all keys with undefined values because Firestore does not support the undefined type
      // Also remove the id field since it's stored in the document path
      if (entity[key] !== undefined && key !== IdField) {
        sanitized[key] = deepSanitize(entity[key], isPreservedType);
      }
      return sanitized;
    },
    {} as Record<string, unknown>,
  );
}

/**
 * Substitutes ServerTimestampSentinelValue with actual serverTimestamp() FieldValue
 * for all time fields in the given object recursively.
 *
 * @param obj the object to process.
 * @param createServerTimestamp factory function to create a server timestamp.
 * @param isPreservedType predicate to check if a value should be preserved as-is.
 * @returns a copy of the object with sentinel values replaced.
 */
export function substituteServerTimestampsRecursive(
  obj: Record<string, unknown>,
  createServerTimestamp: CreateServerTimestamp,
  isPreservedType: IsPreservedType,
): Record<string, unknown> {
  const result: Record<string, unknown> = {...obj};
  Object.keys(result).forEach((key) => {
    const value = result[key];
    if ((key.endsWith('Time') || key.endsWith('At')) && value === ServerTimestampSentinelValue) {
      result[key] = createServerTimestamp();
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value) && !isPreservedType(value)) {
      result[key] = substituteServerTimestampsRecursive(
        value as Record<string, unknown>,
        createServerTimestamp,
        isPreservedType,
      );
    }
  });
  return result;
}

/**
 * Substitutes ServerTimestampSentinelValue with actual serverTimestamp() FieldValue
 * for all time fields in the metadata object.
 *
 * @param metadata the metadata object containing time fields.
 * @param createServerTimestamp factory function to create a server timestamp.
 * @param isPreservedType predicate to check if a value should be preserved as-is.
 * @returns a new metadata object with sentinel values replaced.
 */
export function substituteServerTimestampInMetadata(
  metadata: Record<string, unknown>,
  createServerTimestamp: CreateServerTimestamp,
  isPreservedType: IsPreservedType,
): Record<string, unknown> {
  return substituteServerTimestampsRecursive(metadata, createServerTimestamp, isPreservedType);
}

/**
 * Substitutes ServerTimestampSentinelValue with actual serverTimestamp() FieldValue
 * for time fields in an entity's metadata.
 *
 * @param entity the entity containing a metadata field.
 * @param createServerTimestamp factory function to create a server timestamp.
 * @param isPreservedType predicate to check if a value should be preserved as-is.
 * @returns a new entity with sentinel values in metadata replaced.
 */
export function substituteServerTimestamp<T extends Record<string, unknown>>(
  entity: T,
  createServerTimestamp: CreateServerTimestamp,
  isPreservedType: IsPreservedType,
): T {
  const metadata = entity[MetadataField] as Record<string, unknown> | undefined;
  if (!metadata) {
    return entity;
  }
  return {
    ...entity,
    [MetadataField]: substituteServerTimestampInMetadata(metadata, createServerTimestamp, isPreservedType),
  };
}
